import React, { FC, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { CustomTimePicker } from './ScheduleTimePicker.styles';

interface ScheduleTimePickerProps {
  onChange?: (time: Dayjs | null) => void;
  defaultValue?: Dayjs | null;
  selectedDate?: Dayjs | null;
  disabled?: boolean;
}

const ScheduleTimePicker: FC<ScheduleTimePickerProps> = ({
  onChange,
  defaultValue,
  selectedDate,
  disabled,
}) => {
  const { t } = useTranslation();
  // styled() drops antd's picker generic, so the value arrives typed as unknown
  const handleTimeChange = (time: unknown) => {
    if (onChange) onChange(time as Dayjs | null);
  };

  const disabledTime = () => {
    const now = dayjs();
    const isToday = selectedDate?.isSame(now, 'day');

    let disabledHours = Array.from({ length: 24 }, (_, i) => i).filter(h => h < 8);

    if (isToday) {
      const pastHours = Array.from({ length: now.hour() }, (_, i) => i);
      disabledHours = Array.from({ length: 24 }, (_, i) => i).filter(
        h => pastHours.includes(h) || h < 8
      );
    }

    const disabledMinutesFn = (selectedHour: number) => {
      if (isToday && selectedHour === now.hour()) {
        return Array.from({ length: now.minute() }, (_, i) => i);
      }
      return [];
    };

    return {
      disabledHours: () => disabledHours,
      disabledMinutes: disabledMinutesFn,
    };
  };

  const patchNowButton = () => {
    setTimeout(() => {
      const timePanel = document.querySelector('.ant-picker-time-panel-container');
      if (!timePanel) return;

      const nowButton = timePanel.querySelector('.ant-picker-now-btn') as HTMLElement;
      if (nowButton) {
        nowButton.onclick = e => {
          e.preventDefault();
          e.stopPropagation();
          const customTime = dayjs().add(15, 'minute').startOf('minute');
          onChange?.(customTime);
        };
      }
    }, 0);
  };

  useEffect(() => {
    const observer = new MutationObserver(patchNowButton);
    const popupContainer = document.querySelector('.ant-picker-dropdown');
    if (popupContainer) {
      observer.observe(popupContainer, { childList: true, subtree: true });
    }
    return () => observer.disconnect();
  }, []);

  return (
    <CustomTimePicker
      data-testid="schedule-time-picker"
      value={defaultValue}
      onOpenChange={open => {
        if (open) patchNowButton();
      }}
      onChange={handleTimeChange}
      format="HH:mm"
      defaultOpenValue={dayjs('08:00:00', 'HH:mm:ss')}
      disabledTime={disabledTime}
      minuteStep={15}
      disabled={disabled}
      placeholder={t('shared.timePicker.placeholder')}
    />
  );
};

export default ScheduleTimePicker;
